import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import axios from 'axios'
import * as Icons from 'react-feather'
import DataTable from 'react-data-table-component'
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts'
import { getApiBaseUrl } from '../../utils/apiConfig'
import { logAuditEvent } from '../../utils/auditLogger'
import { ReactComponent as SortArrow } from '../../shared/img/chevron-up-down.svg'

const HandoverRegisterMessage = forwardRef(({ handoverRegisterData = {}, onCancel = () => {}, onSent = () => {}, moduleName = 'Handover Register' }, ref) => {
  const [activeTab, setActiveTab] = useState('email')
  const [fromEmail, setFromEmail] = useState('')
  const [toEmail, setToEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [fromMobile, setFromMobile] = useState('')
  const [toMobile, setToMobile] = useState('')
  const [whatsAppMessage, setWhatsAppMessage] = useState('')
  const [files, setFiles] = useState([])
  const [selectedFileIds, setSelectedFileIds] = useState([])
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [alert, setAlert] = useState(null)

  const handoverRegisterId = handoverRegisterData?.HandoverRegisterId

  useEffect(() => {
    const title = handoverRegisterData?.CustomerName || handoverRegisterData?.SalesNumber || handoverRegisterId
    setSubject(`Handover Register Documents - ${title || ''}`.trim())
  }, [handoverRegisterData?.CustomerName, handoverRegisterData?.SalesNumber, handoverRegisterId])

  useEffect(() => {
    const defaultMobile = handoverRegisterData?.MobileNumber || ''
    if (defaultMobile && !toMobile) {
      setToMobile(defaultMobile)
    }
  }, [handoverRegisterData?.MobileNumber, toMobile])

  useEffect(() => {
    const lines = [
      'Handover Register Details',
      handoverRegisterData?.CustomerName ? `Customer: ${handoverRegisterData.CustomerName}` : '',
      handoverRegisterData?.SalesNumber ? `Sales#: ${handoverRegisterData.SalesNumber}` : ''
    ].filter(Boolean)
    if (!whatsAppMessage) {
      setWhatsAppMessage(lines.join('\n'))
    }
  }, [handoverRegisterData?.CustomerName, handoverRegisterData?.SalesNumber, whatsAppMessage])

  useEffect(() => {
    const fetchData = async () => {
      if (!handoverRegisterId) return
      setLoading(true)
      try {
        const [fromValue, fromPhone, fileList] = await Promise.all([
          fetchFromEmail(),
          fetchFromMobile(),
          fetchHandoverFiles(handoverRegisterId)
        ])
        setFromEmail(fromValue || '')
        setFromMobile(fromPhone || '')
        setToEmail(handoverRegisterData?.EmailId || '')
        setFiles(fileList)
        setSelectedFileIds(fileList.filter((f) => f.FileLibraryId).map((f) => f.FileLibraryId))
      } catch (error) {
        console.error(error)
        CreateAlert('Unable to load email defaults', 'Error', setAlert)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [handoverRegisterId, handoverRegisterData?.EmailId])

  useImperativeHandle(ref, () => ({
    submit: () => (activeTab === 'whatsapp' ? handleSendWhatsApp() : handleSendEmail())
  }))

  const fetchFromEmail = async () => {
    try {
      const res = await axios.get(getApiBaseUrl() + '/clientsettings/getall')
      const payload = res?.data?.data
      const list = Array.isArray(payload?.[0]) ? payload[0] : Array.isArray(payload) ? payload : []
      const match = (list || []).find((item) => (item?.SettingParameter || '').toLowerCase().includes('email'))
      return match?.Value || ''
    } catch (error) {
      console.error(error)
      return ''
    }
  }

  const fetchFromMobile = async () => {
    try {
      const res = await axios.get(getApiBaseUrl() + '/clientsettings/getall')
      const payload = res?.data?.data
      const list = Array.isArray(payload?.[0]) ? payload[0] : Array.isArray(payload) ? payload : []
      const match = (list || []).find((item) => {
        const key = (item?.SettingParameter || '').toLowerCase()
        return key.includes('mobile') || key.includes('phone')
      })
      return match?.Value || ''
    } catch (error) {
      console.error(error)
      return ''
    }
  }

  const fetchHandoverFiles = async (registerId) => {
    try {
      const res = await axios.get(getApiBaseUrl() + '/handoverregister/getfiles', {
        params: { HandoverRegisterId: registerId }
      })
      const payload = res?.data?.data
      return Array.isArray(payload) ? payload : Array.isArray(payload?.[0]) ? payload[0] : []
    } catch (error) {
      console.error(error)
      return []
    }
  }

  const toggleFileSelection = (fileId, checked) => {
    setSelectedFileIds((prev) => {
      if (checked) return Array.from(new Set([...(prev || []), fileId]))
      return (prev || []).filter((id) => id !== fileId)
    })
  }

  const handleSendEmail = async () => {
    if (sending) return
    if (!toEmail) {
      CreateAlert('Recipient email is required', 'Warning', setAlert)
      return
    }
    setSending(true)
    try {
      await axios.post(getApiBaseUrl() + '/handoverregister/sendemail', {
        HandoverRegisterId: handoverRegisterId,
        fromEmail,
        toEmail,
        subject,
        description,
        attachmentFileLibraryIds: selectedFileIds
      })
      CreateAlert('Email sent successfully', 'Success', setAlert)
      logAuditEvent(moduleName, 'Email Sent', JSON.stringify({
        Id: handoverRegisterId,
        To: toEmail,
        Count: selectedFileIds.length
      }))
      onSent()
    } catch (error) {
      console.error(error)
      CreateAlert(error?.response?.data?.message || 'Unable to send email', 'Error', setAlert)
    } finally {
      setSending(false)
    }
  }

  const handleSendWhatsApp = async () => {
    if (sending) return
    if (!toMobile) {
      CreateAlert('Recipient mobile number is required', 'Warning', setAlert)
      return
    }
    if (!whatsAppMessage) {
      CreateAlert('WhatsApp message is required', 'Warning', setAlert)
      return
    }
    setSending(true)
    try {
      await axios.post(getApiBaseUrl() + '/whatsapp/send', {
        toMobile,
        message: whatsAppMessage,
        attachmentFileLibraryIds: selectedFileIds
      })
      CreateAlert('WhatsApp sent successfully', 'Success', setAlert)
      logAuditEvent(moduleName, 'WhatsApp Sent', JSON.stringify({
        Id: handoverRegisterId,
        To: toMobile,
        Count: selectedFileIds.length
      }))
      onSent()
    } catch (error) {
      console.error(error)
      CreateAlert(error?.response?.data?.message || 'Unable to send WhatsApp', 'Error', setAlert)
    } finally {
      setSending(false)
    }
  }

  const columns = [
    {
      name: <div className="titlename"><span>File Title</span></div>,
      selector: row => row.FileTitle,
      cell: (row) => (
        <span title={row.FileTitle || ''}>{row.FileTitle || '-'}</span>
      )
    },
    {
      name: <div className="titlename"><span>File Name</span></div>,
      selector: row => row.OriginalName,
      cell: (row) => (
        <span title={row.OriginalName || ''}>{row.OriginalName || '-'}</span>
      )
    },
    {
      name: <div className="titlename"><span>Category</span></div>,
      selector: row => row.FileCategory,
      cell: (row) => (
        <span>{row.FileCategory || '-'}</span>
      )
    },
    {
      name: <div className="titlename"><span>Include</span></div>,
      cell: (row) => (
        <input
          type="checkbox"
          checked={selectedFileIds.includes(row.FileLibraryId)}
          onChange={(e) => toggleFileSelection(row.FileLibraryId, e.target.checked)}
        />
      ),
      center: true,
      width: '100px'
    }
  ]

  return (
    <>
      <SKAlerts alert={alert} />
      <div className="border rounded p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex gap-2">
            <button
              type="button"
              className={`btn btn-sm ${activeTab === 'email' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setActiveTab('email')}
            >
              <Icons.Mail size={14} className="me-1" /> Email
            </button>
            <button
              type="button"
              className={`btn btn-sm ${activeTab === 'whatsapp' ? 'btn-success' : 'btn-outline-success'}`}
              onClick={() => setActiveTab('whatsapp')}
            >
              <Icons.MessageCircle size={14} className="me-1" /> WhatsApp
            </button>
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={onCancel}>Close</button>
        </div>

        {activeTab === 'email' && (
          <>
            <div className="row">
              <div className="col-lg-6 col-sm-12 mb-3">
                <label className="form-label">From</label>
                <input className="form-control" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} />
              </div>
              <div className="col-lg-6 col-sm-12 mb-3">
                <label className="form-label">To</label>
                <input className="form-control" value={toEmail} onChange={(e) => setToEmail(e.target.value)} />
              </div>
              <div className="col-lg-12 mb-3">
                <label className="form-label">Subject</label>
                <input className="form-control" value={subject} onChange={(e) => setSubject(e.target.value)} />
              </div>
              <div className="col-lg-12 mb-3">
                <label className="form-label">Message</label>
                <textarea className="form-control" rows="3" value={description} onChange={(e) => setDescription(e.target.value)}></textarea>
              </div>
            </div>
            <div className="border rounded p-2">
              <div className="fw-semibold mb-2">Attachments</div>
              <DataTable
                columns={columns}
                data={files}
                progressPending={loading}
                pagination
                sortIcon={<SortArrow />}
                persistTableHead
              />
            </div>
            <div className="mt-3 d-flex justify-content-end">
              <button className="btn btn-primary" onClick={handleSendEmail} disabled={sending}>
                {sending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </>
        )}

        {activeTab === 'whatsapp' && (
          <>
            <div className="row">
              <div className="col-lg-6 col-sm-12 mb-3">
                <label className="form-label">From Mobile</label>
                <input className="form-control" value={fromMobile} onChange={(e) => setFromMobile(e.target.value)} />
              </div>
              <div className="col-lg-6 col-sm-12 mb-3">
                <label className="form-label">To Mobile</label>
                <input className="form-control" value={toMobile} onChange={(e) => setToMobile(e.target.value)} />
              </div>
              <div className="col-lg-12 mb-3">
                <label className="form-label">Message</label>
                <textarea className="form-control" rows="3" value={whatsAppMessage} onChange={(e) => setWhatsAppMessage(e.target.value)}></textarea>
              </div>
            </div>
            <div className="border rounded p-2">
              <div className="fw-semibold mb-2">Attachments</div>
              <DataTable
                columns={columns}
                data={files}
                progressPending={loading}
                pagination
                sortIcon={<SortArrow />}
                persistTableHead
              />
            </div>
            <div className="mt-3 d-flex justify-content-end">
              <button className="btn btn-success" onClick={handleSendWhatsApp} disabled={sending}>
                {sending ? 'Sending...' : 'Send WhatsApp'}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  )
})

HandoverRegisterMessage.displayName = 'HandoverRegisterMessage'

export default HandoverRegisterMessage
